/**
 * Bonsai Mail - Rules Management Page
 * Create and manage email categorization rules
 */

import { useEffect, useState } from "react";
import { useBonsai } from "../../contexts/BonsaiContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Plus,
  Settings2,
  Pencil,
  Trash2,
  Zap,
  ArrowRightLeft,
  Forward,
  Loader2,
  Tag,
  ExternalLink
} from "lucide-react";
import { format } from "date-fns";
import { fmtUK } from "../../lib/dateUtils";

const categories = [
  "Urgent",
  "Client Communication",
  "Court Documents",
  "Opposing Counsel",
  "Internal",
  "Newsletter/Marketing",
  "Spam",
  "Compliance",
  "Finance"
];

const actions = [
  { value: "none", label: "No action", icon: Tag },
  { value: "move", label: "Move to folder", icon: ArrowRightLeft },
  { value: "forward", label: "Forward to email", icon: Forward },
  { value: "delete", label: "Delete", icon: Trash2 },
];

const categoryColors = {
  "Urgent": "bg-status-red/10 text-status-red border-red-500/20",
  "Client Communication": "bg-status-green/10 text-status-green border-green-500/20",
  "Court Documents": "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  "Opposing Counsel": "bg-purple-500/10 text-purple-600 border-purple-500/20",
  "Internal": "bg-navy-hover/600/10 text-slate-600 border-slate-500/20",
  "Newsletter/Marketing": "bg-slate-400/10 text-slate-500 border-slate-400/20",
  "Spam": "bg-status-red/10 text-status-red border-red-400/20",
  "Compliance": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Finance": "bg-teal/10 text-teal border-teal/20"
};

export default function BonsaiRules() {
  const { authenticated, bonsaiApi } = useBonsai();
  
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteRuleId, setDeleteRuleId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    keywords: "",
    category: "",
    action: "none",
    action_target: "",
    is_active: true
  });

  useEffect(() => {
    if (authenticated) {
      fetchRules();
    }
  }, [authenticated]);

  const fetchRules = async () => {
    try {
      const response = await bonsaiApi.get("/rules");
      setRules(response.data);
    } catch (e) {
      console.error("Failed to fetch rules:", e);
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      keywords: "",
      category: "",
      action: "none",
      action_target: "",
      is_active: true
    });
    setEditingRule(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || "",
      keywords: (rule.keywords || []).join(", "),
      category: rule.category,
      action: rule.action,
      action_target: rule.action_target || "",
      is_active: rule.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.category) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        keywords: formData.keywords.split(",").map(k => k.trim()).filter(k => k),
        category: formData.category,
        action: formData.action,
        action_target: formData.action_target || null,
        is_active: formData.is_active
      };

      if (editingRule) {
        await bonsaiApi.put(`/rules/${editingRule.id}`, payload);
        toast.success("Rule updated");
      } else {
        await bonsaiApi.post("/rules", payload);
        toast.success("Rule created");
      }

      fetchRules();
      setDialogOpen(false);
      resetForm();
    } catch (e) {
      console.error("Failed to save rule:", e);
      toast.error("Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRuleId) return;
    
    try {
      await bonsaiApi.delete(`/rules/${deleteRuleId}`);
      toast.success("Rule deleted");
      fetchRules();
    } catch (e) {
      console.error("Failed to delete rule:", e);
      toast.error("Failed to delete rule");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteRuleId(null);
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await bonsaiApi.put(`/rules/${rule.id}`, {
        ...rule,
        is_active: !rule.is_active
      });
      fetchRules();
      toast.success(`Rule ${!rule.is_active ? 'activated' : 'deactivated'}`);
    } catch (e) {
      console.error("Failed to toggle rule:", e);
      toast.error("Failed to update rule");
    }
  };

  if (!authenticated) {
    return (
      <div className="p-8 text-center">
        <Zap className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Please sign in to manage rules</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8" data-testid="rules-loading">
        <div className="mb-8">
          <Skeleton className="h-10 w-48 mb-2" />
          <Skeleton className="h-5 w-72" />
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="bonsai-rules-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Categorization Rules
          </h1>
          <p className="text-slate-500">
            Define rules for automatic email categorization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://bonsai-caseminder-portal.online"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors"
            data-testid="hub-link-rules"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            CaseMinder Hub
          </a>
          <Button 
            onClick={handleOpenCreate} 
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            data-testid="create-rule-button"
          >
            <Plus className="w-4 h-4" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card className="border-navy-border">
          <CardContent className="py-12 text-center">
            <Settings2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-white mb-2">No rules yet</h3>
            <p className="text-slate-500 mb-4">
              Create your first rule to help AI categorize your emails
            </p>
            <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="rules-list">
          {rules.map((rule, index) => {
            const ActionIcon = actions.find(a => a.value === rule.action)?.icon || Tag;
            
            return (
              <Card 
                key={rule.id}
                className={`border-navy-border hover:shadow-md transition-shadow ${!rule.is_active ? 'opacity-60' : ''}`}
                data-testid={`rule-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-md ${rule.is_active ? 'bg-status-amber-dim' : 'bg-slate-100'} flex items-center justify-center`}>
                          <Zap className={`w-5 h-5 ${rule.is_active ? 'text-status-amber' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">
                            {rule.name}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {rule.description || "No description"}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-4">
                        <Badge className={categoryColors[rule.category] || "bg-slate-100 text-slate-600"}>
                          {rule.category}
                        </Badge>
                        
                        {rule.keywords?.length > 0 && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <span>Keywords:</span>
                            {rule.keywords.slice(0, 3).map((keyword, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {keyword}
                              </Badge>
                            ))}
                            {rule.keywords.length > 3 && (
                              <span>+{rule.keywords.length - 3} more</span>
                            )}
                          </div>
                        )}
                        
                        {rule.action !== "none" && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <ActionIcon className="w-3 h-3" />
                            <span className="capitalize">{rule.action}</span>
                            {rule.action_target && <span>→ {rule.action_target}</span>}
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-slate-400 mt-3">
                        Created {rule.created_at ? fmtUK(rule.created_at, "PP") : "recently"}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => handleToggleActive(rule)}
                        data-testid={`rule-toggle-${index}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(rule)}
                        data-testid={`edit-rule-${index}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setDeleteRuleId(rule.id); setDeleteDialogOpen(true); }}
                        className="text-status-red hover:text-status-red hover:bg-status-red-dim"
                        data-testid={`delete-rule-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? "Edit Rule" : "Create Rule"}
            </DialogTitle>
            <DialogDescription>
              {editingRule ? "Update your categorization rule" : "Define a new rule for email categorization"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Client Emails"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="rule-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Brief description of this rule"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="keywords">Keywords (comma-separated)</Label>
              <Input
                id="keywords"
                placeholder="client, consultation, meeting"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                data-testid="rule-keywords-input"
              />
              <p className="text-xs text-slate-500">
                Emails containing these keywords will match this rule
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="rule-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="action">Action</Label>
              <Select
                value={formData.action}
                onValueChange={(value) => setFormData({ ...formData, action: value })}
              >
                <SelectTrigger data-testid="rule-action-select">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  {actions.map((action) => (
                    <SelectItem key={action.value} value={action.value}>
                      <div className="flex items-center gap-2">
                        <action.icon className="w-4 h-4" />
                        {action.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(formData.action === "move" || formData.action === "forward") && (
              <div className="space-y-2">
                <Label htmlFor="action_target">
                  {formData.action === "move" ? "Folder Name" : "Email Address"}
                </Label>
                <Input
                  id="action_target"
                  placeholder={formData.action === "move" ? "Client Emails" : "paralegal@bonsai.law"}
                  value={formData.action_target}
                  onChange={(e) => setFormData({ ...formData, action_target: e.target.value })}
                />
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active</Label>
                <p className="text-xs text-slate-500">
                  Enable or disable this rule
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-rule-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingRule ? "Update Rule" : "Create Rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this rule? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-status-red text-white hover:bg-status-red"
              data-testid="confirm-delete-rule"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
